import styled, { css } from 'styled-components';
import Button from 'components/Button';
import Title from 'components/Title';
import Text from 'components/Text';
import { colorVariables, fontVariables, mediaTemplates } from 'theme/variables';

const matchPropsToStyle = (styleOptions) => (props) => {
  const [, style] = styleOptions.find(([test]) => test(props));
  return style;
};

export const StyledButton = styled(Button)`
  && {
    padding: 8px 18px 13px;

    ${({ isMobile }) =>
      isMobile &&
      css`
        padding: 0px 7px 0px;
      `};

    ${({ showBackOnAir, isMobile }) =>
      showBackOnAir &&
      isMobile &&
      css`
        letter-spacing: 1px;
        padding: 1px 3px 2px;
        margin-right: 7px;
        border-radius: 5px;
        border: solid 1px ${colorVariables.gray_smokegray};
        opacity: 0.7;
      `};
  }
  ${({ isBlocked }) =>
    isBlocked &&
    css`
      opacity: 0.2;
      cursor: default !important;
      pointer-events: none; /* to disable hover */
    `};
`;

export const CatchUp = styled(Text)`
  text-shadow: 0 0 5px ${colorVariables.gray_shadow};
  margin-right: 4px;
`;

export const VideoDescription = styled(Text)`
  opacity: 0.5;
`;

export const LeftPartTextContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

export const RightPartButtonWrap = styled.div`
  margin-right: 4px;

  &:last-child {
    margin-right: 0;
  }
`;

export const PlayerSkinStyled = styled.div`
  position: relative;
  width: 100%;
  flex-grow: 1;
  flex-shrink: 0;
  opacity: 1;
  visibility: visible;
  transition: opacity 0.25s;

  * {
    box-sizing: border-box;
  }

  ${({ isMobile, isFullscreen }) =>
    isMobile &&
    css`
      ${TopWrap} {
        margin-top: ${isFullscreen ? '15px' : '10px'};
      }
      ${RightPartButtonWrap} {
        margin-right: 0;
      }
    `};
  ${({ isHidden }) =>
    isHidden &&
    css`
      opacity: 0;
      visibility: hidden;
    `};

  ${({ isSmallMode }) => css`
    overflow: ${isSmallMode ? 'initial' : 'hidden'};
  `};
`;

export const MainWrapper = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 2;
  opacity: 1;
  transition: opacity 0.5s;

  ${({ isFullscreen, isTV, isDesktopEpgPresent, isSmallMode }) => css`
    padding-right: ${isFullscreen && isTV && isDesktopEpgPresent && !isSmallMode
      ? '55px'
      : '0px'};
  `}

  ${({ isNeedHideOnEnd }) =>
    isNeedHideOnEnd &&
    css`
      opacity: 0;
      visibility: hidden;
    `};
`;

export const EndedWrap = styled.div`
  cursor: pointer;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  padding: 20px;
  z-index: 2;
  background-color: rgba(0, 0, 0, 0.5);
`;

export const EndedTopPartWrap = styled.div`
  position: relative;
  z-index: 10;
`;

export const RightSeek = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: 0;
  transition: 135ms ease-in-out;

  ${(props) =>
    props.rightSeek &&
    css`
      opacity: 0.5;
    `};
`;

export const LeftSeek = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: 0;
  transition: 135ms ease-in-out;

  ${(props) =>
    props.leftSeek &&
    css`
      opacity: 0.5;
    `};
`;

export const Substrate = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 15%;

  ${({ hideSkin }) =>
    hideSkin &&
    css`
      cursor: none;
    `};

  ${({ smallMode, hideSkin }) =>
    smallMode &&
    css`
      background-color: black;
      transition: opacity 0.3s ease-in;
      cursor: ${hideSkin ? 'default' : 'pointer'};
      opacity: ${hideSkin ? 0 : 0.3};
    `};
`;

export const PlayerSkinWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  opacity: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
  flex-shrink: 0;
  visibility: visible;
  transition:
    opacity 275ms ease-in-out,
    visibility 275ms ease-in-out;

  ${(props) =>
    props.isHidden &&
    css`
      opacity: 0;
      visibility: hidden;
    `};
`;

export const LeftPart = styled.div`
  position: relative;
`;

export const TopPartStyled = styled.div`
  position: relative;
  margin: 0 40px;

  ${({ smallMode, isMobile }) =>
    (smallMode || isMobile) &&
    css`
      margin: 0 15px;
    `};

  ${({ isSmallMode, isLinearHybridAdShowed }) =>
    isSmallMode &&
    isLinearHybridAdShowed &&
    css`
      position: absolute;
      width: 100%;
      margin: 0;
      left: 0;
      bottom: -6px;
    `};
`;

export const RightPart = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
`;

export const TVControlsStyled = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

export const SeriesControlsStyled = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

export const VideoControlsStyled = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

export const BackButton = styled(Button)`
  padding-right: 10px;
`;

export const TimebarWrap = styled.div`
  position: absolute;
  bottom: 18px;
  left: 0;
  z-index: 1;
  display: flex;
  align-items: center;

  ${({ isMobile }) =>
    isMobile &&
    css`
      padding-bottom: 5px;
    `};
  ${({ isForcePointerEvents }) =>
    isForcePointerEvents &&
    css`
      pointer-events: all;
    `}
`;

export const SeekbarWrap = styled.div`
  position: relative;
  margin-top: 0;

  ${({ isMobile }) =>
    isMobile &&
    css`
      padding-bottom: 5px;
    `};

  ${({ isDisabled }) =>
    isDisabled &&
    css`
      pointer-events: none;
    `};
`;

export const PlayerSkinFooterStyled = styled.div`
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  ${({ smallMode, isMobile }) =>
    (smallMode || isMobile) &&
    css`
      padding-bottom: 7px;
    `};
`;

export const PlayerSkinFooterWrapStyled = styled.div`
  margin: 0 18px;
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  z-index: 2;

  ${({ smallMode, isMobile }) =>
    (smallMode || isMobile) &&
    css`
      margin: 0 7px;
    `};

  /*
  // Can't find any reason for shifting skin footer.
  transform: translate(-14px, 0);
  
  ${({ isMobile }) =>
    isMobile &&
    css`
      transform: translate(-10px, 0);
    `};
*/
`;

export const CenterPartStyled = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

export const SettingsWrap = styled.div`
  position: relative;
`;

const subtitlesFontSize = [
  [
    ({ fullscreen }) => fullscreen,
    css`
      ${mediaTemplates.xs} {
        font-size: ${fontVariables.fs_l};
      }
      ${mediaTemplates.m} {
        font-size: ${fontVariables.fs_xxxl};
      }
      ${mediaTemplates.xxl} {
        font-size: ${fontVariables.fs_xxxxl};
      }
    `,
  ],
  [
    ({ fullscreen }) => !fullscreen,
    css`
      ${mediaTemplates.xs} {
        font-size: ${fontVariables.fs_xxxs};
      }
      ${mediaTemplates.m} {
        font-size: ${fontVariables.fs_xxxl};
      }
      ${mediaTemplates.xxl} {
        font-size: ${fontVariables.fs_xxxxl};
      }
    `,
  ],
];

export const SubtitlesStyled = styled.div`
  position: absolute;
  bottom: 70px;
  width: 90%;
  left: 50%;
  transform: translateX(-50%);
  padding: 10px;
  color: white;
  font-size: ${fontVariables.fs_xl};
  text-align: center;
  text-shadow: 0 0 5px #000;
  transition: 95ms ease-in-out;
  opacity: 0.9;
  white-space: pre-wrap;
  pointer-events: none;
  z-index: 0;

  ${matchPropsToStyle(subtitlesFontSize)}
`;

export const LiveLabel = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 5px;
`;

export const LiveDot = styled.span`
  position: relative;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #ff3333;
  margin-right: 5px;
`;

export const LiveText = styled.span`
  position: relative;
  color: white;
  font-size: ${fontVariables.fs_xxs};
  display: flex;
  flex-direction: row;
`;

export const RightPartIconWrap = styled.div`
  position: relative;
`;

export const CenterPartIconWrap = styled.div`
  position: relative;

  ${(props) =>
    props.hidden &&
    css`
      opacity: 0;
      visibility: hidden;
    `};
`;

export const WaterMarkWrap = styled.div`
  position: absolute;
  right: 18px;
  bottom: 20px;
  width: 100px;
  z-index: 1;
  transition: all 0.25s ease-in-out;

  ${(props) =>
    props.topPosition &&
    css`
      transform: ${props.isMobile
        ? `translate(0, -55px)`
        : `translate(0, -45px)`};
    `};
`;

export const Description = styled.div`
  margin-top: 3px;
  z-index: 10;

  ${({ isMobTrailer }) =>
    isMobTrailer &&
    css`
      margin-top: 0;
      text-align: left;
    `};
`;

export const MessageContainerStyled = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  ${({ visible }) =>
    !visible &&
    css`
      display: none !important;
    `};
`;

export const ShareContainer = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  overflow: auto;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  font-size: 0;
`;

export const StyledTitle = styled(Title)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${({ isAdTitle }) =>
    isAdTitle &&
    css`
      opacity: 0.6;
    `};
`;

export const TopWrap = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  flex-direction: row;
  justify-content: space-between;
  padding: 20px 28px 0 35px;
  z-index: 2;

  ${LeftPart} {
    ${({ isMobile }) =>
      isMobile
        ? css`
            margin-top: 0px;
          `
        : css`
            margin-top: 10px;
          `};

    max-width: 60%;
    display: flex;
    justify-content: flex-start;
    align-items: self-start;
  }
  ${RightPart} {
    transform: translate(8px, 0);
    flex-shrink: 0;
    margin-left: 30px;
  }
  ${StyledButton} {
    ${({ isMobile }) =>
      isMobile
        ? css`
            padding: 3px 12px;
          `
        : css`
            padding: 13px;
          `}
  }

  ${(props) =>
    props.isMobile &&
    css`
      padding: ${props.isFullscreen ? '0 15px' : '0 10px'};
      ${LeftPart} {
        position: absolute;
        top: 0;
        left: ${props.isFullscreen ? '5px' : '45px'};
        right: ${props.offsetValue ? `${props.offsetValue * 40}px` : '10px'};
        padding-right: 5px;
      }
      ${StyledTitle} {
        display: ${props.isFullscreen && props.isMobTrailer ? 'none' : 'block'};
        font-size: 14px;
        opacity: 1;
        white-space: unset;
        margin-top: 2px;
        ${props.isTV &&
        css`
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        `}
      }
      ${RightPart} {
        margin-left: auto;
      }
    `};
`;

export const EmojiButtonsContainer = styled.div`
  ${({ applyMediaQuery }) =>
    applyMediaQuery &&
    css`
      ${mediaTemplates.xxxs} {
        display: none;
      }
    `};
`;
